import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarcodeExportConfigurationContext } from '../../../../context/BarcodeExportConfigurationContext';
import { useToast } from '../../../../providers/ToastProvider';
import { BarcodeExportConfigurationRequest } from '../../../../requests/barcodeExportConfiguration.request';
import { useRequest } from '../../../../requests/common/requestHook';
import {
  BarcodeExportConfiguration,
  BarcodeExportConfigurationInput,
} from '../../../../types/barcode_export_configuration';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { translate } from '../../../../types/translation/Translator';
import { Routing } from '../../../../util/routes.util';
import { CancelButton, DeleteButton } from '../../../components/Button';
import { AlertModal } from '../../../components/Modal';
import { ToastType } from '../../../components/Toast';

interface BarcodeExportConfigurationDeleteModalProps {
  configurations: BarcodeExportConfiguration[];
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function BarcodeExportConfigurationDeleteModal({
  configurations,
  open,
  setOpen,
}: BarcodeExportConfigurationDeleteModalProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { barcodeExportConfigurations, setBarcodeExportConfigurations } = useContext(BarcodeExportConfigurationContext);
  const [remove, { loading }] = useRequest(BarcodeExportConfigurationRequest.remove);

  const handleDelete = async () => {
    const response = await remove({
      data: configurations.map(c => new BarcodeExportConfigurationInput(c).forDelete()),
    });
    if (!response) return;

    navigate(Routing.settings.barcodes.overview());
    showToast({
      type: ToastType.success,
      title: translate({ key: TranslationKey.barcodeExportConfigurationDeletedSuccessfullyToast }),
    });

    response.forEach(c => barcodeExportConfigurations.delete(c.id));
    setBarcodeExportConfigurations(new Map(barcodeExportConfigurations));
  };

  return (
    <AlertModal open={open} setOpen={setOpen} title={'Delete barcode export configuration?'}>
      <div className='grid gap-4'>
        <div className='grid gap-2'>
          <p>Are you sure you want to delete the selected barcode export configurations?</p>
        </div>
        <div className='flex justify-end gap-2'>
          <CancelButton onClick={() => setOpen(false)} disabled={loading} />
          <DeleteButton onClick={handleDelete} loading={loading} />
        </div>
      </div>
    </AlertModal>
  );
}
